import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { CampoBusca } from '../componentes/CampoBusca.js'
import { api, type ChecklistAdmin } from '../dados/api.js'
import { PainelAdmin, QuandoFoi, SemRedeAqui } from './admin-comum.js'

/// Os checklists do sistema, para o administrador montar novos a partir do
/// catálogo de campos. Só admin, e só com rede.
///
/// Os 141 que vieram do Power Apps ficam aqui para consulta e para servir de base:
/// "Duplicar" abre o editor com os campos dele, e o que sai é um checklist do
/// sistema, que se edita à vontade.

type Filtro = 'ativos' | 'sistema' | 'powerapps' | 'desativados'

export function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export function Checklists() {
  const navegar = useNavigate()
  const aviso = (useLocation().state as { aviso?: string } | null)?.aviso
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('ativos')
  const lista = useQuery({ queryKey: ['admin', 'checklists'], queryFn: () => api.adminChecklists() })

  const visiveis = useMemo(() => {
    const termo = semAcento(busca.trim())
    return (lista.data ?? []).filter((c) => {
      if (filtro === 'ativos' && !c.ativo) return false
      if (filtro === 'desativados' && c.ativo) return false
      if (filtro === 'sistema' && c.origem !== 'SISTEMA') return false
      if (filtro === 'powerapps' && c.origem !== 'POWER_APPS') return false
      return !termo || semAcento(`${c.codigo ?? ''} ${c.nome} ${c.categoria ?? ''}`).includes(termo)
    })
  }, [lista.data, busca, filtro])

  const doSistema = (lista.data ?? []).filter((c) => c.origem === 'SISTEMA').length

  return (
    <PainelAdmin titulo="Checklists" subtitulo="Monte checklists com as perguntas do catálogo de campos">
      {aviso && (
        <p className="mb-3 rounded bg-leve/10 px-3 py-2 text-sm text-leve" role="status">
          {aviso}
        </p>
      )}

      <button onClick={() => navegar('/checklists/novo')} className="botao-primario mb-3 w-full !min-h-0 !py-2 text-sm lg:w-auto">
        + Novo checklist
      </button>

      <div className="cartao mb-4 space-y-3 p-3 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-center lg:gap-2 lg:space-y-0">
        <CampoBusca valor={busca} aoMudar={setBusca} placeholder="Buscar por código ou nome" />
        <select value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)} className="campo">
          <option value="ativos">Ativos</option>
          <option value="sistema">Criados no sistema ({doSistema})</option>
          <option value="powerapps">Vieram do Power Apps</option>
          <option value="desativados">Desativados</option>
        </select>
      </div>

      {lista.isError && <SemRedeAqui erro={lista.error} />}
      {lista.isLoading && <p className="py-8 text-center text-slate-500">Carregando…</p>}

      {lista.data && (
        <>
          <p className="mb-2 text-sm text-slate-600">
            {visiveis.length === 0
              ? 'Nenhum checklist com esses filtros.'
              : `${visiveis.length} ${visiveis.length === 1 ? 'checklist' : 'checklists'}`}
          </p>
          <ul className="grid gap-2 lg:grid-cols-2 lg:items-start">
            {visiveis.map((c) => (
              <li key={c.id}>
                <Cartao checklist={c} />
              </li>
            ))}
          </ul>
        </>
      )}
    </PainelAdmin>
  )
}

function Cartao({ checklist: c }: { checklist: ChecklistAdmin }) {
  const cliente = useQueryClient()
  const [confirmando, setConfirmando] = useState(false)
  const alternar = useMutation({
    mutationFn: () => api.adminAtivarChecklist(c.id, !c.ativo),
    onSuccess: () => {
      setConfirmando(false)
      void cliente.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  return (
    <div className={`cartao p-3 ${c.ativo ? '' : 'opacity-70'}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {c.codigo && <span className="etiqueta bg-marinho-50 font-mono text-marinho">{c.codigo}</span>}
        {c.origem === 'SISTEMA' ? (
          <span className="etiqueta bg-limao/20 text-marinho-900">Criado no sistema</span>
        ) : (
          <span className="etiqueta bg-slate-100 text-slate-600">Power Apps</span>
        )}
        {!c.ativo && <span className="etiqueta bg-moderada/10 text-moderada">Desativado</span>}
      </div>
      <p className="mt-1.5 font-semibold text-slate-800">{c.nome}</p>
      <p className="mt-0.5 text-xs text-slate-500">
        {c.campos} {c.campos === 1 ? 'campo' : 'campos'} · {c.inspecoes.toLocaleString('pt-BR')}{' '}
        {c.inspecoes === 1 ? 'inspeção' : 'inspeções'}
        {c.origem === 'SISTEMA' && (
          <>
            {' · alterado '}
            <QuandoFoi iso={c.atualizadoEm} />
          </>
        )}
      </p>

      {confirmando ? (
        <div className="mt-3 rounded-lg border border-moderada/30 bg-moderada/5 p-3 text-sm">
          <p className="text-slate-700">
            {c.ativo
              ? 'O checklist sai da lista de quem abre inspeção. As inspeções já feitas nele continuam no histórico; um rascunho aberto nele, num aparelho, fica sem o formulário até o checklist ser reativado.'
              : 'O checklist volta para a lista de quem abre inspeção, com os mesmos campos.'}
          </p>
          {alternar.isError && <p className="mt-2 text-alta">{(alternar.error as Error).message}</p>}
          <div className="mt-2 flex gap-2">
            <button onClick={() => alternar.mutate()} disabled={alternar.isPending} className="botao-primario flex-1 !min-h-0 !py-1.5 text-sm">
              {alternar.isPending ? 'Salvando…' : c.ativo ? 'Desativar' : 'Reativar'}
            </button>
            <button
              onClick={() => {
                setConfirmando(false)
                alternar.reset()
              }}
              className="botao-neutro flex-1 !min-h-0 !py-1.5 text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Link to={`/checklists/${c.id}`} className="botao-neutro !min-h-0 !py-1.5 text-sm">
            {c.editavel ? 'Editar' : 'Ver campos'}
          </Link>
          {c.tipo !== 'INI' && (
            <Link to={`/checklists/novo?de=${c.id}`} className="botao-neutro !min-h-0 !py-1.5 text-sm">
              Duplicar
            </Link>
          )}
          {c.editavel && (
            <button onClick={() => setConfirmando(true)} className="botao-neutro !min-h-0 !py-1.5 text-sm">
              {c.ativo ? 'Desativar' : 'Reativar'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
